import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_VIDEO_ADS } from '../graphql/queries';
import { useNavigate } from 'react-router-dom';
import { getAbsoluteImageUrl } from '../lib/imageUtils';
import { HeartIcon, ChatBubbleLeftIcon, ShareIcon, PlayIcon, PauseIcon, XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';
import { Button, Badge } from 'react-bootstrap';

interface Story {
  id: string;
  title: string;
  slug: string;
  price: string;
  priceType: string;
  videoUrl: string;
  videoThumbnail: string;
  videoDuration: number;
  description: string;
  location: string;
  city: string;
  condition: string;
  isFeatured: boolean;
  publishedAt: string;
  viewCount: number;
  favoriteCount: number;
  user: {
    id: string;
    username: string;
    displayName: string;
    profilePicture: string;
    phone: string;
    email: string;
  };
  category: {
    id: string;
    name: string;
    slug: string;
  };
}

const Stories: React.FC = () => {
  const navigate = useNavigate();
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [likedStories, setLikedStories] = useState<Set<string>>(new Set());
  const [showDetails, setShowDetails] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const storyContainerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data, loading, error } = useQuery(GET_VIDEO_ADS, {
    variables: { limit: 20 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const stories = data?.videoAds || [];

  // Auto-progress for story viewing
  useEffect(() => {
    if (!isPlaying || isPaused || showDetails || !stories[currentStoryIndex]) return;

    const currentStory = stories[currentStoryIndex];
    const duration = currentStory?.videoDuration || 10; // Default to 10 seconds
    const increment = 100 / (duration * 10); // Update every 100ms

    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          // Move to next story
          nextStory();
          return 0;
        }
        return prev + increment;
      });
    }, 100);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoryIndex, isPlaying, isPaused, showDetails, stories]);

  // Handle video play/pause
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentStoryIndex && isPlaying && !isPaused && !showDetails) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    });
  }, [currentStoryIndex, isPlaying, isPaused, showDetails, stories.length]);

  const nextStory = () => {
    if (currentStoryIndex < stories.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
      setProgress(0);
    }
  };

  const prevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
      setProgress(0);
    }
  };

  const handleLike = (storyId: string) => {
    setLikedStories(prev => {
      const newLiked = new Set(prev);
      if (newLiked.has(storyId)) {
        newLiked.delete(storyId);
      } else {
        newLiked.add(storyId);
      }
      return newLiked;
    });
  };

  const handleShare = async (story: Story) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: story.title,
          text: `Check out this story ad on NayaBajar!`,
          url: `${window.location.origin}/ad/${story.slug}`,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    videoRefs.current.forEach(video => {
      if (video) video.muted = !isMuted;
    });
  };

  // Touch handling for swipe gestures
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    // Minimum swipe distance
    const minSwipeDistance = 50;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal swipe - show/hide details
      if (Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          // Swipe right - hide details
          setShowDetails(false);
        } else {
          // Swipe left - show details
          setShowDetails(true);
        }
      }
    } else {
      // Vertical swipe - navigate stories
      if (Math.abs(deltaY) > minSwipeDistance) {
        if (deltaY > 0) {
          // Swipe down - previous story
          prevStory();
        } else {
          // Swipe up - next story
          nextStory();
        }
      }
    }

    touchStartRef.current = null;
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPrice = (price: string, priceType: string) => {
    if (priceType === 'free') return 'Free';
    if (priceType === 'swap') return 'Swap';
    if (price) return `Rs. ${parseInt(price).toLocaleString()}`;
    return 'Contact';
  };

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
        <div className="d-flex gap-2">
          <div style={{ width: '12px', height: '12px', backgroundColor: '#3b82f6', borderRadius: '50%' }}></div>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#3b82f6', borderRadius: '50%' }}></div>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#3b82f6', borderRadius: '50%' }}></div>
        </div>
      </div>
    );
  }

  if (error || stories.length === 0) {
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
        <div className="text-center text-white px-4">
          <p className="fs-5 mb-4">No stories available</p>
          <Button
            onClick={() => navigate('/')}
            variant="primary"
          >
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const currentStory = stories[currentStoryIndex];

  return (
    <div
      ref={containerRef}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'black', overflow: 'hidden' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close Button */}
      <Button
        onClick={() => navigate(-1)}
        style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          zIndex: 50,
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(4px)',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          padding: 0
        }}
      >
        <XMarkIcon style={{ width: '24px', height: '24px' }} />
      </Button>

      {/* Progress Bars */}
      <div style={{ position: 'absolute', top: '1rem', left: '1rem', right: '4rem', zIndex: 40, display: 'flex', gap: '4px' }}>
        {stories.map((_: any, index: number) => (
          <div
            key={index}
            style={{ flex: 1, height: '2px', backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '9999px', overflow: 'hidden' }}
          >
            <div
              style={{
                height: '100%',
                backgroundColor: 'white',
                transition: 'width 100ms',
                width: index < currentStoryIndex ? '100%' : index === currentStoryIndex ? `${progress}%` : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Story Container */}
      <div
        ref={storyContainerRef}
        style={{ height: '100%', width: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {/* Video/Story Content */}
        <div style={{ position: 'relative', height: '100%', width: '100%', maxWidth: '28rem', margin: '0 auto' }}>
          {/* Video */}
          <video
            ref={el => videoRefs.current[currentStoryIndex] = el}
            src={currentStory?.videoUrl}
            style={{ height: '100%', width: '100%', objectFit: 'contain' }}
            loop
            playsInline
            muted={isMuted}
            onClick={togglePlayPause}
            poster={currentStory?.videoThumbnail}
          />

          {/* Pause Overlay */}
          {isPaused && !showDetails && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: 'rgba(0, 0, 0, 0.2)'
              }}
              onClick={togglePlayPause}
            >
              <PauseIcon style={{ width: '80px', height: '80px', color: 'white' }} />
            </div>
          )}

          {/* Play Overlay */}
          {!isPlaying && !showDetails && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: 'rgba(0, 0, 0, 0.2)'
              }}
              onClick={togglePlayPause}
            >
              <PlayIcon style={{ width: '80px', height: '80px', color: 'white' }} />
            </div>
          )}

          {/* Swipe Hint */}
          {!showDetails && (
            <div style={{ position: 'absolute', bottom: '8rem', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
              <div style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(4px)', borderRadius: '9999px', padding: '0.5rem 1rem' }}>
                <p style={{ color: 'white', fontSize: '0.75rem', margin: 0 }}>
                  ← Swipe for details | Swipe ↑↓ for more →
                </p>
              </div>
            </div>
          )}

          {/* Mute Button */}
          <Button
            onClick={toggleMute}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '5rem',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(4px)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 40,
              padding: 0
            }}
          >
            {isMuted ? (
              <span style={{ fontSize: '1rem' }}>🔇</span>
            ) : (
              <span style={{ fontSize: '1rem' }}>🔊</span>
            )}
          </Button>

          {/* Story Info Overlay */}
          {!showDetails && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.6), transparent)', padding: '1rem' }}>
              {/* User Info */}
              <div className="d-flex align-items-center gap-3 mb-3">
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#374151', overflow: 'hidden' }}>
                  {currentStory?.user.profilePicture ? (
                    <img
                      src={getAbsoluteImageUrl(currentStory.user.profilePicture) || ''}
                      alt={currentStory.user.displayName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold' }}>
                      {currentStory.user.displayName?.[0]}
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ color: 'white', fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>@{currentStory?.user.username}</p>
                  <p style={{ color: '#d1d5db', fontSize: '0.75rem', margin: 0 }}>{formatDistanceToNow(new Date(currentStory?.publishedAt || ''), { addSuffix: true })}</p>
                </div>
              </div>

              {/* Title & Price */}
              <h2 style={{ color: 'white', fontWeight: 'bold', fontSize: '1.125rem', marginBottom: '0.5rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{currentStory?.title}</h2>
              {currentStory?.price && (
                <Badge bg="primary" className="mb-2" style={{ display: 'inline-block' }}>
                  <span style={{ fontWeight: 'bold' }}>{formatPrice(currentStory.price, currentStory.priceType)}</span>
                </Badge>
              )}

              {/* Description */}
              {currentStory?.description && (
                <p style={{ color: '#e5e7eb', fontSize: '0.875rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: '0.5rem' }}>{currentStory.description}</p>
              )}

              {/* Location */}
              <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>{currentStory?.city || currentStory?.location}</p>
            </div>
          )}

          {/* Details Panel (Left Swipe) */}
          {showDetails && (
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to left, rgba(0,0,0,0.95), rgba(0,0,0,0.9), transparent)', overflowY: 'auto' }}>
              <div style={{ height: '100%', overflowY: 'auto', padding: '1.5rem', paddingTop: '5rem' }}>
                <Button
                  onClick={() => setShowDetails(false)}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(4px)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    padding: 0
                  }}
                >
                  <XMarkIcon style={{ width: '20px', height: '20px' }} />
                </Button>

                <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>{currentStory?.title}</h2>

                {/* Price */}
                {currentStory?.price && (
                  <Badge bg="primary" className="mb-3" style={{ padding: '0.5rem 1rem' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '1.125rem' }}>{formatPrice(currentStory.price, currentStory.priceType)}</span>
                  </Badge>
                )}

                {/* Condition */}
                {currentStory?.condition && (
                  <div className="mb-3">
                    <Badge bg="success" className="text-capitalize">
                      {currentStory.condition.replace('_', ' ')}
                    </Badge>
                  </div>
                )}

                {/* Description */}
                {currentStory?.description && (
                  <div className="mb-4">
                    <h3 style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Description</h3>
                    <p style={{ color: 'white', margin: 0 }}>{currentStory.description}</p>
                  </div>
                )}

                {/* Location */}
                <div className="mb-4">
                  <h3 style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Location</h3>
                  <p style={{ color: 'white', margin: 0 }}>{currentStory?.city}, {currentStory?.location}</p>
                </div>

                {/* Category */}
                {currentStory?.category && (
                  <div className="mb-4">
                    <h3 style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Category</h3>
                    <p style={{ color: 'white', margin: 0 }}>{currentStory.category.name}</p>
                  </div>
                )}

                {/* Stats */}
                <div className="row g-3 mb-4">
                  <div className="col-4 text-center">
                    <p style={{ color: 'white', fontWeight: 'bold', fontSize: '1.125rem', margin: 0 }}>{currentStory?.viewCount || 0}</p>
                    <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>Views</p>
                  </div>
                  <div className="col-4 text-center">
                    <p style={{ color: 'white', fontWeight: 'bold', fontSize: '1.125rem', margin: 0 }}>{currentStory?.favoriteCount || 0}</p>
                    <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>Likes</p>
                  </div>
                  <div className="col-4 text-center">
                    <p style={{ color: 'white', fontWeight: 'bold', fontSize: '1.125rem', margin: 0 }}>
                      {currentStory?.videoDuration ? formatDuration(currentStory.videoDuration) : '--'}
                    </p>
                    <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>Duration</p>
                  </div>
                </div>

                {/* Contact */}
                <div className="d-grid gap-2">
                  <h3 style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Contact Seller</h3>
                  {currentStory?.user.phone && (
                    <a
                      href={`tel:${currentStory.user.phone}`}
                      className="btn btn-success py-2 fw-semibold"
                    >
                      Call Seller
                    </a>
                  )}
                  <Button
                    onClick={() => navigate(`/ad/${currentStory?.slug}`)}
                    variant="primary"
                    className="py-2 fw-semibold"
                  >
                    View Full Ad
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Right Side Actions */}
          {!showDetails && (
            <div style={{ position: 'absolute', right: '1rem', bottom: '8rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', zIndex: 40 }}>
              {/* Like */}
              <button
                onClick={() => currentStory && handleLike(currentStory.id)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <div style={{ width: '48px', height: '48px', backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(4px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {likedStories.has(currentStory?.id || '') ? (
                    <HeartIconSolid style={{ width: '28px', height: '28px', color: '#ef4444' }} />
                  ) : (
                    <HeartIcon style={{ width: '28px', height: '28px', color: 'white' }} />
                  )}
                </div>
                <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 600 }}>
                  {(currentStory?.favoriteCount || 0) + (likedStories.has(currentStory?.id || '') ? 1 : 0)}
                </span>
              </button>

              {/* Comment */}
              <button
                onClick={() => currentStory && navigate(`/ad/${currentStory.slug}`)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <div style={{ width: '48px', height: '48px', backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(4px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChatBubbleLeftIcon style={{ width: '28px', height: '28px', color: 'white' }} />
                </div>
                <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 600 }}>Comment</span>
              </button>

              {/* Share */}
              <button
                onClick={() => currentStory && handleShare(currentStory)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <div style={{ width: '48px', height: '48px', backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(4px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShareIcon style={{ width: '28px', height: '28px', color: 'white' }} />
                </div>
                <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 600 }}>Share</span>
              </button>

              {/* Details */}
              <button
                onClick={() => setShowDetails(true)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <div style={{ width: '48px', height: '48px', backgroundColor: '#2563eb', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
                  <InformationCircleIcon style={{ width: '28px', height: '28px', color: 'white' }} />
                </div>
                <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 600 }}>Details</span>
              </button>
            </div>
          )}

          {/* Bottom Progress */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
            <div
              style={{
                height: '100%',
                backgroundColor: '#2563eb',
                transition: 'width 300ms',
                width: `${((currentStoryIndex + 1) / stories.length) * 100}%`
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stories;
